<template>
  <v-card class="renew-modal pw-flush">
    <!-- Cargando opciones -->
    <template v-if="loadingOptions">
      <v-card-title class="headline d-flex justify-space-between align-center">
        Renovar membresía
        <v-btn icon aria-label="Cerrar" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-3 mb-0">Cargando opciones de renovación...</p>
      </v-card-text>
    </template>

    <!-- Error al cargar opciones -->
    <template v-else-if="optionsError">
      <v-card-title class="headline d-flex justify-space-between align-center">
        Renovar membresía
        <v-btn icon aria-label="Cerrar" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pt-6">
        <v-alert type="error" text dense class="mb-0">{{ optionsError }}</v-alert>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn text color="grey darken-1" @click="closeDialog">Cerrar</v-btn>
        <v-btn color="primary" text @click="fetchOptions">Reintentar</v-btn>
      </v-card-actions>
    </template>

    <!-- Formulario + resumen -->
    <div v-else-if="options" class="renew-modal__layout">
      <!-- Columna izquierda: formulario -->
      <div class="renew-modal__form">
        <div class="renew-modal__header d-flex align-start justify-space-between">
          <div>
            <div class="text-h6 font-weight-bold">Renovar membresía</div>
            <div class="text-body-2 grey--text text--darken-1">
              A la derecha ves qué pasará al renovar.
            </div>
          </div>
          <v-btn icon aria-label="Cerrar" :disabled="submitting" class="mt-n1 mr-n2" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="renew-modal__body">

        <!-- Contrato (solo contratos donde el usuario tiene acceso y cupo) -->
        <v-select
          v-model="selectedContratoId"
          :items="contractItems"
          item-text="label"
          item-value="id"
          label="Contrato"
          outlined
          dense
          hide-details="auto"
          :loading="loadingContracts || loadingPlans"
          :disabled="loadingPlans || contractItems.length <= 1"
          @change="onContractChange"
        ></v-select>
        <v-checkbox
          v-if="contractChanged && membershipContrato"
          v-model="downgradePreviousAccess"
          :label="`Bajar el acceso al contrato ${membershipContrato.codigo}`"
          dense
          hide-details
          class="mt-2"
        ></v-checkbox>
        <v-alert v-if="plansError" type="error" text dense class="mt-3 mb-0">{{ plansError }}</v-alert>

        <v-select
          v-model="selectedPlanId"
          :items="options.plans"
          item-text="name"
          item-value="id"
          label="Nuevo plan *"
          outlined
          dense
          hide-details="auto"
          class="mt-5"
          :disabled="loadingPlans"
          :hint="planHint"
          :persistent-hint="Boolean(planHint)"
        >
          <!-- Los planes desactivados del contrato también se ofrecen (el backend
               los manda al final), para poder renovar sobre un plan que ya no
               se vende; se marcan para que no se elijan por error. -->
          <template v-slot:item="{ item }">
            <span>{{ item.name }}</span>
            <v-chip v-if="item.active === false" x-small outlined color="grey darken-1" class="ml-2">
              Desactivado
            </v-chip>
          </template>
          <template v-slot:selection="{ item }">
            <span>{{ item.name }}</span>
            <v-chip v-if="item.active === false" x-small outlined color="grey darken-1" class="ml-2">
              Desactivado
            </v-chip>
          </template>
        </v-select>

        <v-row dense class="mt-4">
          <v-col cols="6" class="py-0">
            <v-select
              v-model="selectedInterval"
              :items="intervalOptions"
              item-text="label"
              item-value="value"
              label="Intervalo de pago"
              outlined
              dense
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="6" class="py-0">
            <!-- Contrato con descuento para el intervalo: solo lectura -->
            <v-text-field
              v-if="options.hasContract && !specialDiscountAllowed"
              :value="contractDiscountText"
              label="Descuento de contrato"
              outlined
              dense
              readonly
              hide-details="auto"
              hint="Definido por el contrato, no editable"
              persistent-hint
            ></v-text-field>

            <!-- B2C, o contrato con 0% en el intervalo: descuento especial vía código -->
            <v-select
              v-else
              v-model="selectedCodeId"
              :items="discountCodeItems"
              item-text="label"
              item-value="id"
              :label="codeSelectLabel"
              :hint="specialDiscountAllowed ? 'El contrato no otorga descuento para este intervalo' : ''"
              :persistent-hint="specialDiscountAllowed"
              outlined
              dense
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col cols="6" class="py-0">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="startDateText"
                  label="Fecha de inicio *"
                  outlined
                  dense
                  readonly
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startDate" @change="onStartDateChange"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6" class="py-0">
            <v-menu
              v-model="graceDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="graceDateText"
                  label="Fecha de carencia"
                  hint="Se autorellena con la fecha de inicio; editable"
                  persistent-hint
                  outlined
                  dense
                  readonly
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startGraceDays" @input="graceDateMenu = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <!-- La suscripción de MP no existe o está cancelada: se ofrece renovar con link -->
        <v-alert v-if="subscriptionFallback" type="warning" outlined dense class="mt-5 mb-0">
          <div class="font-weight-bold">
            Esta membresía no tiene una suscripción activa en Mercado Pago
          </div>
          <div class="text-body-2 mt-1">{{ subscriptionFallback }}</div>
          <div class="text-body-2 mt-2">
            ¿Quieres renovar generando un link de pago? La membresía quedará pendiente de pago
            hasta que el usuario pague.
          </div>
          <div class="d-flex justify-end mt-3">
            <v-btn text small :disabled="submitting" @click="subscriptionFallback = null">
              No, volver
            </v-btn>
            <v-btn
              color="warning darken-3"
              small
              depressed
              class="ml-2"
              :loading="submitting"
              @click="confirmRenewal(true)"
            >
              Generar link y renovar
            </v-btn>
          </div>
        </v-alert>

        <v-alert v-if="submitError" type="error" text dense class="mt-5 mb-0">
          <strong v-if="submitError.code">{{ submitError.code }}: </strong>
          <span v-html="submitErrorHtml"></span>
        </v-alert>
        </div>

        <v-divider></v-divider>
        <v-card-actions class="justify-end px-6 py-4">
          <v-btn text color="primary" rounded :disabled="submitting" @click="closeDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            rounded
            depressed
            :loading="submitting"
            :disabled="!canSubmit"
            @click="confirmRenewal"
          >
            Renovar
          </v-btn>
        </v-card-actions>
      </div>

      <!-- Columna derecha: resumen -->
      <aside class="renew-summary d-flex flex-column" aria-label="Resumen de la renovación">
        <div class="renew-summary__eyebrow">Resumen · renovación</div>
        <div>
          <div class="renew-summary__name">{{ options.membership.petName }}</div>
          <div class="renew-summary__muted">Ref. {{ options.membership.ref }}</div>
        </div>

        <div class="renew-summary__card renew-summary__card--old">
          <div class="renew-summary__card-label">Se desactiva</div>
          <div>{{ options.membership.planName }} · {{ currentIntervalLabel }}</div>
          <div class="renew-summary__muted">{{ currentContractText }}</div>
          <div class="renew-summary__muted">
            Vigencia {{ formatIsoDate(options.membership.startDate) }} –
            {{ formatIsoDate(options.membership.expirationDate) }}
          </div>
          <div>
            <span class="renew-summary__price renew-summary__price--small">
              {{ formatPrice(options.membership.currentAmount) }}
            </span>
            <span class="renew-summary__muted"> {{ options.membership.currency }}</span>
          </div>
        </div>

        <div class="renew-summary__card renew-summary__card--new">
          <div class="renew-summary__card-label">Nueva membresía · desde {{ startDateText }}</div>
          <div>
            <template v-if="selectedPlan">{{ selectedPlan.name }} · {{ intervalLabel }}</template>
            <span v-else class="renew-summary__new-muted">Selecciona un plan</span>
          </div>
          <div class="renew-summary__new-muted">{{ selectedContractText }}</div>
          <div v-if="priceInfo" class="d-flex align-baseline flex-wrap">
            <span class="renew-summary__price">{{ formatPrice(priceInfo.finalPrice) }}</span>
            <span class="renew-summary__new-muted ml-1">
              {{ priceInfo.currency }} / {{ isAnnual ? 'año' : 'mes' }}
            </span>
            <span v-if="priceInfo.hasDiscount" class="renew-summary__badge ml-auto">
              -{{ priceInfo.discountPercent }}%
            </span>
          </div>
        </div>

        <div v-if="needsPaymentLink" class="renew-summary__notice">
          Se generará un link de pago para enviar al usuario; la membresía quedará pendiente de
          pago hasta que pague.
        </div>

        <v-spacer></v-spacer>

        <div class="renew-summary__facts">
          <div class="renew-summary__facts-title">Qué pasa al renovar</div>
          <div class="d-flex justify-space-between">
            <span>Mercado Pago</span>
            <span class="text-right ml-4">{{ mercadoPagoSummary }}</span>
          </div>
          <div v-if="previousAccessSummary" class="d-flex justify-space-between">
            <span>Acceso {{ membershipContrato.codigo }}</span>
            <span class="text-right ml-4">{{ previousAccessSummary }}</span>
          </div>
          <div v-if="contractChanged" class="d-flex justify-space-between">
            <span>Acceso nuevo</span>
            <span class="text-right ml-4">Usa 1 cupo</span>
          </div>
        </div>
      </aside>
    </div>
  </v-card>
</template>

<script>
import MembershipsService from '../services/memberships.service';
import CurrencyUtils from '../utils/currency.utils';

const GENERIC_NETWORK_ERROR =
  'No se pudo completar la renovación, revisa tu conexión e intenta de nuevo';

// Valor del selector de contrato para una membresía sin contrato (B2C puro)
const NO_CONTRACT = '__no_contract__';

// Rechazos del backend cuando la suscripción de MP actual no existe o está
// cancelada; con un plan nuevo cobrado por MP se ofrece renovar con link de pago
// Errores cuyo mensaje del backend viene en inglés (los comparte con la API pública).
const ERROR_MESSAGES = {
  PLAN_NOT_ALLOWED_FOR_ACCESS: 'El acceso del usuario a este contrato no permite ese plan.'
};

const SUBSCRIPTION_FALLBACK_REASONS = {
  MP_SUBSCRIPTION_NOT_FOUND: 'No se encontró su suscripción en Mercado Pago.',
  MP_SUBSCRIPTION_INVALID_STATE: 'Su suscripción en Mercado Pago está cancelada.'
};

export default {
  name: 'RenewMembershipModal',
  props: {
    // Membresía seleccionada en la tabla del perfil (requiere al menos `id`)
    membership: { type: Object, required: true },
    // Id (Postgres) del dueño; habilita el cambio de contrato
    userId: { type: String, default: null }
  },
  data() {
    return {
      loadingOptions: false,
      optionsError: null,
      options: null,
      selectedPlanId: null,
      selectedCodeId: null,
      // Intervalo de pago seleccionado (1 = anual, 2 = mensual)
      selectedInterval: null,
      startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      startDateMenu: false,
      // Origen de la carencia (startGraceDays). Por defecto se precarga
      // con el startDate de la membresía original, pero es editable.
      startGraceDays: null,
      graceDateMenu: false,
      submitting: false,
      submitError: null,
      // Contratos del usuario con acceso y cupo (GET /memberships/user-contracts)
      userContracts: [],
      loadingContracts: false,
      selectedContratoId: NO_CONTRACT,
      loadingPlans: false,
      plansError: null,
      downgradePreviousAccess: false,
      // Motivo por el que no hay suscripción de MP usable; muestra la confirmación
      subscriptionFallback: null
    };
  },
  created() {
    this.fetchOptions();
  },
  watch: {
    // Si el código deja de estar disponible (p.ej. al cambiar a un intervalo que
    // el contrato sí descuenta), se limpia para no enviarlo ni mostrar precio stale
    showCodeSelect(canShow) {
      if (!canShow) this.selectedCodeId = null;
    }
  },
  computed: {
    // Intervalo SELECCIONADO en el formulario; rige descuentos y monto referencial
    isAnnual() {
      return this.selectedInterval === 1;
    },
    intervalLabel() {
      return this.isAnnual ? 'anual' : 'mensual';
    },
    // Intervalo original de la membresía; solo para el bloque "Se desactiva"
    currentIntervalLabel() {
      return this.options && this.options.membership.paymentInterval === 1
        ? 'anual'
        : 'mensual';
    },
    // Empresa/contrato de la membresía; tolera respuestas antiguas sin los campos
    membershipCompany() {
      return (this.options && this.options.membership.company) || null;
    },
    membershipContrato() {
      return (this.options && this.options.membership.contrato) || null;
    },
    // Empresa y contrato actuales en texto, para el resumen
    currentContractText() {
      if (!this.membershipContrato) {
        return this.membershipCompany ? this.membershipCompany.name : 'Sin empresa (B2C)';
      }
      return this.membershipCompany
        ? `${this.membershipCompany.name} · ${this.membershipContrato.codigo}`
        : this.membershipContrato.codigo;
    },
    // Valor del selector que representa el contrato actual de la membresía
    currentContractValue() {
      return this.membershipContrato ? this.membershipContrato.id : NO_CONTRACT;
    },
    contractChanged() {
      return this.selectedContratoId !== this.currentContractValue;
    },
    // Contrato destino a enviar al backend; null = se mantiene el actual
    targetContratoId() {
      return this.contractChanged ? this.selectedContratoId : null;
    },
    contractItems() {
      const others = this.userContracts
        .filter(contract => contract.contratoId !== this.currentContractValue)
        .map(contract => ({ id: contract.contratoId, label: contract.displayName }));
      return [
        { id: this.currentContractValue, label: `${this.currentContractText} (actual)` },
        ...others
      ];
    },
    // Contrato en el que quedará la nueva membresía, en texto
    selectedContractText() {
      if (!this.contractChanged) return this.currentContractText;
      const item = this.contractItems.find(contract => contract.id === this.selectedContratoId);
      return item ? item.label : '';
    },
    // true cuando el backend creará un link de pago: el nuevo plan se cobra con
    // MercadoPago y cambia el intervalo o el plan actual se cobra por planilla
    needsPaymentLink() {
      return Boolean(
        this.options &&
          this.selectedPlan &&
          this.selectedPlanMpManaged &&
          (this.intervalChanged || this.options.membership.mpManaged === false)
      );
    },
    // Qué hará la renovación en MercadoPago (misma matriz que el backend)
    mercadoPagoSummary() {
      const oldMpManaged = !this.options || this.options.membership.mpManaged !== false;
      const newMpManaged = this.selectedPlanMpManaged;
      if (this.needsPaymentLink) {
        return oldMpManaged
          ? 'Nuevo link de pago; se cancela la suscripción actual'
          : 'Nuevo link de pago (hoy se cobra por planilla)';
      }
      if (oldMpManaged && newMpManaged) return 'Se actualiza la suscripción actual';
      if (oldMpManaged) return 'Se cancela la suscripción; el nuevo plan va por planilla';
      return 'No aplica: se cobra por planilla';
    },
    // Qué le pasa al acceso del contrato actual; null cuando no hay contrato actual
    previousAccessSummary() {
      if (!this.membershipContrato) return null;
      const accounts = this.options.membership.accessTotalAccounts;
      if (!this.contractChanged || !this.downgradePreviousAccess) {
        return accounts ? `Se mantiene (${accounts} ${accounts === 1 ? 'cuenta' : 'cuentas'})` : 'Se mantiene';
      }
      if (accounts && accounts > 1) return `${accounts} → ${accounts - 1} cuentas`;
      return '1 → 0 cuentas (oculto)';
    },
    intervalChanged() {
      return Boolean(
        this.options && this.selectedInterval !== this.options.membership.paymentInterval
      );
    },
    intervalOptions() {
      if (!this.options) return [];
      const currentIsAnnual = this.options.membership.paymentInterval === 1;
      return [
        { label: `Mensual${currentIsAnnual ? '' : ' (actual)'}`, value: 2 },
        { label: `Anual${currentIsAnnual ? ' (actual)' : ''}`, value: 1 }
      ];
    },
    selectedPlan() {
      if (!this.options || !this.selectedPlanId) return null;
      return this.options.plans.find(plan => plan.id === this.selectedPlanId) || null;
    },
    // true si el plan seleccionado opera con MercadoPago; sin plan seleccionado
    // (o respuestas antiguas sin el campo) se asume true para no alarmar
    selectedPlanMpManaged() {
      if (!this.selectedPlan) return true;
      return this.selectedPlan.mpManaged !== false;
    },
    planHint() {
      const hints = [];
      if (this.selectedPlan && this.selectedPlan.active === false) {
        hints.push('Plan desactivado en el contrato');
      }
      if (!this.selectedPlanMpManaged) hints.push('Cobro por planilla (no usa MercadoPago)');
      return hints.join(' · ');
    },
    selectedCode() {
      if (!this.options || !this.selectedCodeId) return null;
      return (
        (this.options.availableDiscountCodes || []).find(
          code => code.id === this.selectedCodeId
        ) || null
      );
    },
    contractDiscountText() {
      const discount = this.options && this.options.contractDiscount;
      if (!discount) return 'Sin descuento de contrato';
      return `${Math.round(this.selectedContractFraction * 100)}% (${this.intervalLabel})`;
    },
    // Descuento del contrato (fracción, 0.1 = 10%) para el intervalo seleccionado
    selectedContractFraction() {
      const discount = this.options && this.options.contractDiscount;
      if (!discount) return 0;
      return (this.isAnnual ? discount.annualDiscount : discount.monthlyDiscount) || 0;
    },
    availableCodes() {
      return (this.options && this.options.availableDiscountCodes) || [];
    },
    // Excepción de descuento especial: solo para membresías con contrato cuando el
    // contrato otorga 0% en el intervalo elegido y hay códigos disponibles. El
    // backend aplica el mismo gate por intervalo al renovar.
    specialDiscountAllowed() {
      return Boolean(
        this.options &&
          this.options.hasContract &&
          this.selectedContractFraction === 0 &&
          this.availableCodes.length > 0
      );
    },
    // El selector de código se muestra en B2C o cuando aplica el descuento especial
    showCodeSelect() {
      return Boolean(this.options) && (!this.options.hasContract || this.specialDiscountAllowed);
    },
    codeSelectLabel() {
      return this.specialDiscountAllowed ? 'Descuento especial (opcional)' : 'Descuento (opcional)';
    },
    discountCodeItems() {
      const codes = (this.options && this.options.availableDiscountCodes) || [];
      return [
        { id: null, label: 'Sin descuento' },
        ...codes.map(code => {
          const percent = this.isAnnual ? code.annualPercentOff : code.monthlyPercentOff;
          return { id: code.id, label: `${code.name} (${percent}% desc. ${this.intervalLabel})` };
        })
      ];
    },
    priceInfo() {
      if (!this.selectedPlan) return null;
      const pricing = this.selectedPlan.pricing;
      const original = this.isAnnual ? pricing.originalAnnualPrice : pricing.originalMonthlyPrice;
      let finalPrice = original;
      let discountPercent = 0;
      // El código (B2C o descuento especial sobre contrato 0%) tiene prioridad;
      // de lo contrario rige el descuento del contrato para el intervalo.
      if (this.showCodeSelect && this.selectedCode) {
        discountPercent = this.isAnnual
          ? this.selectedCode.annualPercentOff
          : this.selectedCode.monthlyPercentOff;
        finalPrice = Math.round(original * (1 - discountPercent / 100));
      } else if (this.options.hasContract) {
        finalPrice = this.isAnnual ? pricing.finalAnnualPrice : pricing.finalMonthlyPrice;
        // pricing.xDiscount viene como fracción (0.1 = 10%), no como porcentaje
        const fraction = this.isAnnual ? pricing.annualDiscount : pricing.monthlyDiscount;
        discountPercent = Math.round((fraction || 0) * 100);
      }
      return {
        original,
        finalPrice,
        discountPercent,
        currency: this.selectedPlan.currency,
        hasDiscount: discountPercent > 0 && finalPrice < original
      };
    },
    startDateText() {
      return this.startDate ? this.formatIsoDate(this.startDate) : '';
    },
    graceDateText() {
      return this.startGraceDays ? this.formatIsoDate(this.startGraceDays) : '';
    },
    canSubmit() {
      return (
        Boolean(this.selectedPlanId && this.startDate) && !this.submitting && !this.loadingPlans
      );
    },
    // Renderiza el mensaje de error soportando negritas Markdown (**texto**).
    // Se escapa el HTML antes de convertir, así el v-html es seguro.
    submitErrorHtml() {
      if (!this.submitError || !this.submitError.message) return '';
      const escaped = String(this.submitError.message)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    }
  },
  methods: {
    closeDialog() {
      this.$emit('close-dialog');
    },
    // Hoy en formato YYYY-MM-DD respetando la zona horaria local
    todayIsoDate() {
      return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
    },
    // Toma el día/mes de la fecha de inicio de la membresía y la proyecta al año
    // actual (aniversario de este año). Si no hay fecha, usa hoy.
    buildCurrentYearStartDate(membershipStartDate) {
      const today = this.todayIsoDate();
      if (!membershipStartDate) return today;
      const [, month, day] = String(membershipStartDate).substr(0, 10).split('-');
      if (!month || !day) return today;
      return `${today.substr(0, 4)}-${month}-${day}`;
    },
    async fetchOptions() {
      this.loadingOptions = true;
      this.optionsError = null;
      try {
        const res = await MembershipsService.getRenewalOptions(this.membership.id);
        this.options = res.data;
        this.selectedContratoId = this.currentContractValue;
        this.downgradePreviousAccess = false;
        this.plansError = null;
        this.preselectPlan();
        this.loadUserContracts();
        // Preselecciona "mantener" el intervalo actual de la membresía
        this.selectedInterval = this.options.membership.paymentInterval;
        // Fecha de inicio por defecto = fecha de expiración de la membresía
        // (cuándo vence); si no hay, cae al aniversario de la fecha de inicio.
        this.startDate = this.options.membership.expirationDate
          ? String(this.options.membership.expirationDate).substr(0, 10)
          : this.buildCurrentYearStartDate(this.options.membership.startDate);
        // Carencia por defecto = startDate de la membresía original (editable).
        // No se desplaza al cambiar la fecha de inicio (ver onStartDateChange).
        this.startGraceDays = this.options.membership.startDate
          ? String(this.options.membership.startDate).substr(0, 10)
          : this.startDate;
      } catch (error) {
        this.optionsError =
          (error.isApiError && error.message) ||
          'No se pudieron cargar las opciones de renovación. Revisa tu conexión e intenta de nuevo.';
      } finally {
        this.loadingOptions = false;
      }
    },
    // Preselecciona el plan actual si está entre los planes ofrecidos; limpia el código
    preselectPlan() {
      const currentPlan = this.options.plans.find(
        plan => plan.id === this.options.membership.planId
      );
      this.selectedPlanId = currentPlan ? currentPlan.id : null;
      this.selectedCodeId = null;
    },
    // Contratos a los que se puede mover la membresía: solo donde el usuario tiene
    // acceso visible y cupo. Si falla, el selector queda solo con el contrato actual.
    async loadUserContracts() {
      if (!this.userId) return;
      this.loadingContracts = true;
      try {
        const res = await MembershipsService.getUserContracts(this.userId);
        this.userContracts = res.status === 'ok' ? res.data : [];
      } finally {
        this.loadingContracts = false;
      }
    },
    // Al cambiar de contrato se recargan planes, descuento y códigos de ese contrato
    async onContractChange() {
      this.loadingPlans = true;
      this.plansError = null;
      if (!this.contractChanged) this.downgradePreviousAccess = false;
      try {
        const res = await MembershipsService.getRenewalOptions(
          this.membership.id,
          this.targetContratoId
        );
        this.options = res.data;
        this.preselectPlan();
      } catch (error) {
        this.plansError =
          (error.isApiError && error.message) ||
          'No se pudieron cargar los planes del contrato. Intenta de nuevo.';
        this.selectedPlanId = null;
      } finally {
        this.loadingPlans = false;
      }
    },
    // En renovación, cambiar la fecha de inicio NO desplaza la carencia (a
    // diferencia del editor de membresía). Solo cierra el selector.
    onStartDateChange() {
      this.startDateMenu = false;
    },
    // `YYYY-MM-DD` (o ISO completo) → `DD/MM/YYYY`; se corta el string en vez
    // de pasar por Date para que la zona horaria no corra el día.
    formatIsoDate(value) {
      if (!value) return '—';
      const [year, month, day] = String(value).substr(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },
    formatPrice(amount) {
      if (amount === null || amount === undefined) return '-';
      return `$${CurrencyUtils.formatPriceFromNumber(Math.round(amount))}`;
    },
    // withPaymentLink: el admin confirmó renovar con link de pago aunque la
    // suscripción de MP actual no exista o esté cancelada
    async confirmRenewal(withPaymentLink = false) {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.$emit('submitting', true);
      this.submitError = null;
      this.subscriptionFallback = null;
      try {
        const payload = {
          planId: this.selectedPlanId,
          startDate: this.startDate,
          codeId: this.showCodeSelect && this.selectedCodeId ? this.selectedCodeId : null,
          // 1 = anual, 2 = mensual; el backend trata el valor actual igual que null
          paymentInterval: this.selectedInterval,
          // Origen de la carencia; null deja que el backend use el startDate original
          startGraceDays: this.startGraceDays || null,
          // Contrato destino; null mantiene el contrato actual
          targetContratoId: this.targetContratoId,
          downgradePreviousAccess: this.contractChanged && this.downgradePreviousAccess,
          paymentLinkWithoutSubscription: withPaymentLink === true
        };
        const res = await MembershipsService.renewMembership(this.membership.id, payload);
        this.$emit('renewed', res.data);
      } catch (error) {
        const fallbackReason = error.isApiError && SUBSCRIPTION_FALLBACK_REASONS[error.code];
        if (fallbackReason && this.selectedPlanMpManaged && withPaymentLink !== true) {
          this.subscriptionFallback = fallbackReason;
          return;
        }
        this.submitError = {
          code: error.isApiError ? error.code : null,
          message:
            (error.isApiError && (ERROR_MESSAGES[error.code] || error.message)) ||
            GENERIC_NETWORK_ERROR
        };
      } finally {
        this.submitting = false;
        this.$emit('submitting', false);
      }
    }
  }
};
</script>

<style scoped>
/* Mismo esquema visual que PlanMaintainer.vue (formulario + panel navy lateral) */
.renew-modal {
  overflow: hidden;
}
.renew-modal__layout {
  display: flex;
}
.renew-modal__form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.renew-modal__header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e6e9ef;
}
.renew-modal__body {
  flex-grow: 1;
  padding: 20px 24px;
}
.renew-summary {
  /* Vuetify redondea la esquina inferior izquierda del último hijo de un v-card;
     aquí esa esquina toca el formulario, así que debe ser recta. */
  border-bottom-left-radius: 0 !important;
  width: 340px;
  flex-shrink: 0;
  gap: 18px;
  padding: 24px;
  background: #13233f;
  color: #ffffff;
}
.renew-summary__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a9b8d6;
}
.renew-summary__name {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
}
.renew-summary__muted {
  font-size: 14px;
  color: #c3cee3;
}
.renew-summary__card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-radius: 12px;
  font-size: 14px;
}
.renew-summary__card--old {
  background: #1d3259;
  border: 1px solid #2f4a7a;
}
.renew-summary__card--new {
  background: #ffffff;
  color: #13233f;
}
.renew-summary__card-label {
  font-size: 13px;
  font-weight: 700;
}
.renew-summary__price {
  font-size: 28px;
  font-weight: 800;
}
.renew-summary__price--small {
  font-size: 20px;
}
.renew-summary__new-muted {
  font-size: 13px;
  color: #4a5670;
}
.renew-summary__badge {
  font-size: 12px;
  font-weight: 700;
  color: #145c39;
  background: #dcf2e5;
  padding: 3px 8px;
  border-radius: 999px;
}
.renew-summary__notice {
  padding: 14px 16px;
  border: 1px dashed #f0c36d;
  border-radius: 12px;
  font-size: 13px;
  color: #ffe3a3;
}
.renew-summary__facts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 14px;
  border-top: 1px solid #2f4a7a;
  font-size: 13px;
  color: #c3cee3;
}
.renew-summary__facts-title {
  font-weight: 700;
  color: #ffffff;
}
/* En pantallas angostas el resumen pasa debajo del formulario */
@media (max-width: 760px) {
  .renew-modal__layout {
    flex-direction: column;
  }
  .renew-summary {
    width: auto;
  }
}
</style>
